import React, { useState, useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { calculateRowAverage } from '../utils/pivotHelper';
import { PagesHelper } from '../common/PagesHelper';
import { PublisherOrSiteOption } from '../utils/types';
import dayjs from '../utils/dayjs';
import InfoIcon from '../components/clinks/info/InfoIcon';
import '../datagrid/statisticalHighlighting.css';

interface PerformanceStatsPivotTableProps {
  data: any[]; // Already pivoted data
  rawData: any[]; // Raw API data for expanded view
  dateColumns: string[]; // The date column names (could be dates, weeks, or months)
  viewMode: 'trafficSource' | 'demand';
  trafficSources: PublisherOrSiteOption[];
  demandSources?: any[]; // Demand sources for demand view
  dateColumnKey: string; // The field name to use for date grouping ('date', 'week', or 'month')
  avgColumnTooltipSuffix?: string; // Optional additional text to append to the average column tooltip
}

type SortDirection = 'asc' | 'desc' | null;

const formatCurrency = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCurrency3 = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const formatPercent = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return (num * 100).toFixed(2) + '%';
};

const formatNumber = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  // Truncate decimals to match old dashboard behavior: int(float(v))
  return Math.trunc(num).toLocaleString();
};

// Calculate standard deviation
const calculateStandardDeviation = (values: number[]): number => {
  if (values.length === 0) return 0;
  if (values.length === 1) return values[0];

  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Calculate mean/average
const calculateMean = (values: number[]): number => {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
};

export const PerformanceStatsPivotTable: React.FC<PerformanceStatsPivotTableProps> = ({ data, rawData, dateColumns, viewMode, trafficSources, demandSources = [], dateColumnKey, avgColumnTooltipSuffix }) => {
  const [sortColumn, setSortColumn] = useState<string | null>('_average');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['ALL-ALL']));

  // Helper function to get raw data rows for a specific entity (publisher-site or demand source)
  const getRawDataForEntity = (entityId: string, entitySubId?: string) => {
    if (viewMode === 'demand') {
      // For demand view, filter by campaignSource
      return rawData.filter(row => row.campaignSource === entityId).sort((a, b) => {
        const aDate = a[dateColumnKey] || '';
        const bDate = b[dateColumnKey] || '';
        return bDate.localeCompare(aDate);
      });
    } else {
      // For publisher view, filter by apptapPublisherId and apptapSiteId
      return rawData.filter(row =>
        row.apptapPublisherId === entityId && row.apptapSiteId === entitySubId
      ).sort((a, b) => {
        const aDate = a[dateColumnKey] || '';
        const bDate = b[dateColumnKey] || '';
        return bDate.localeCompare(aDate);
      });
    }
  };

  // Sort the data and filter based on view mode
  const sortedData = useMemo(() => {
    // Filter data based on view mode
    const filteredData = (data || []).filter(row => {
      if (!row || Object.keys(row).length === 0) {
        return false; // Filter out completely empty rows
      }

      if (viewMode === 'demand') {
        // For demand view, filter out rows without valid campaignSource
        // These represent untracked clicks with no campaign association and no actionable demand metrics
        return row.campaignSource != null && row.campaignSource !== '';
      } else {
        // For traffic source view, filter out rows without valid apptapPublisherId
        return row.apptapPublisherId != null && row.apptapPublisherId !== '';
      }
    });

    return [...filteredData].sort((a, b) => {
      let aVal, bVal;

      // Special handling for entity name sorting
      if (sortColumn === 'apptapPublisherName' || sortColumn === 'entityName') {
        if (viewMode === 'demand') {
          // For demand view, use demand source label (without source ID)
          const aSource = demandSources?.find((ds: any) => ds.campaignSourceId === a.campaignSource);
          const bSource = demandSources?.find((ds: any) => ds.campaignSourceId === b.campaignSource);
          aVal = aSource ? `${aSource.partnerName}: ${aSource.sourceDisplayName}` : a.campaignSource || '';
          bVal = bSource ? `${bSource.partnerName}: ${bSource.sourceDisplayName}` : b.campaignSource || '';
        } else {
          // For traffic source view, format as "Publisher: Site"
          aVal = PagesHelper.getTrafficSourceLabel(a.apptapPublisherId, a.apptapSiteId, trafficSources);
          bVal = PagesHelper.getTrafficSourceLabel(b.apptapPublisherId, b.apptapSiteId, trafficSources);
        }
      } else if (sortColumn === '_average') {
        // Calculate average for each row
        aVal = calculateRowAverage(a, dateColumns);
        bVal = calculateRowAverage(b, dateColumns);
      } else {
        // Default: compare raw values
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = -Infinity;
      if (bVal === null || bVal === undefined) bVal = -Infinity;

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Fall back to string comparison (case-insensitive)
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr, undefined, { sensitivity: 'base' })
        : bStr.localeCompare(aStr, undefined, { sensitivity: 'base' });
    });
  }, [data, sortColumn, sortDirection, trafficSources, dateColumns, viewMode, demandSources]);

  // Calculate aggregated summary data from FILTERED rows that will be displayed
  const summaryData = useMemo(() => {
    // Get the entity IDs from filtered/sorted data
    const displayedEntities = new Set<string>();
    sortedData.forEach((row: any) => {
      if (viewMode === 'demand') {
        if (row.campaignSource) displayedEntities.add(row.campaignSource);
      } else {
        const key = `${row.apptapPublisherId}|${row.apptapSiteId}`;
        displayedEntities.add(key);
      }
    });

    // Group raw data by the appropriate date field, but only for displayed entities
    const aggregatedByDate = new Map<string, any>();

    rawData.forEach((row: any) => {
      // Check if this row belongs to a displayed entity
      let isDisplayed = false;
      if (viewMode === 'demand') {
        isDisplayed = displayedEntities.has(row.campaignSource);
      } else {
        const key = `${row.apptapPublisherId}|${row.apptapSiteId}`;
        isDisplayed = displayedEntities.has(key);
      }

      if (!isDisplayed) {
        return; // Skip rows that aren't displayed
      }
      const dateValue = row[dateColumnKey]; // Use the appropriate date field
      if (!aggregatedByDate.has(dateValue)) {
        aggregatedByDate.set(dateValue, {
          date: dateValue, // Keep as 'date' for compatibility with existing code
          revenueApptap: 0,
          revenuePublisher: 0,
          reportedClickCost: 0,
          apptapNetRevenue: 0,
          clicksReceived: 0,
          clicksTracked: 0,
          conversionsReceived: 0,
          conversionsCredited: 0,
          // Demand-specific fields
          wastedClicks: 0,
          distinctProducts: 0,
          workingDistinctProducts: 0,
          brokenDistinctProducts: 0,
          distinctCampaigns: 0,
          workingDistinctCampaigns: 0,
          brokenDistinctCampaigns: 0,
        });
      }

      const agg = aggregatedByDate.get(dateValue);
      agg.revenueApptap += row.revenueApptap || 0;
      agg.revenuePublisher += row.revenuePublisher || 0;
      agg.reportedClickCost += row.reportedClickCost || 0;
      agg.apptapNetRevenue += row.apptapNetRevenue || 0;
      agg.clicksReceived += row.clicksReceived || 0;
      agg.clicksTracked += row.clicksTracked || 0;
      agg.conversionsReceived += row.conversionsReceived || 0;
      agg.conversionsCredited += row.conversionsCredited || 0;
      // Aggregate demand-specific fields
      agg.wastedClicks += row.wastedClicks || 0;
      agg.distinctProducts += row.distinctProducts || 0;
      agg.workingDistinctProducts += row.workingDistinctProducts || 0;
      agg.brokenDistinctProducts += row.brokenDistinctProducts || 0;
      agg.distinctCampaigns += row.distinctCampaigns || 0;
      agg.workingDistinctCampaigns += row.workingDistinctCampaigns || 0;
      agg.brokenDistinctCampaigns += row.brokenDistinctCampaigns || 0;
    });

    // Calculate rates for each date
    aggregatedByDate.forEach((agg: any) => {
      if (agg.clicksReceived > 0) {
        agg.publisherTrackedCreditRate = agg.clicksTracked / agg.clicksReceived;
      } else {
        agg.publisherTrackedCreditRate = null;
      }

      if (agg.conversionsReceived > 0) {
        agg.trackedCreditRate = agg.conversionsCredited / agg.conversionsReceived;
      } else {
        agg.trackedCreditRate = null;
      }

      // Average CR: conversions / clicks (conversion rate)
      if (agg.clicksReceived > 0) {
        agg.averageCR = agg.conversionsReceived / agg.clicksReceived;
      } else {
        agg.averageCR = null;
      }

      if (agg.conversionsCredited > 0) {
        agg.avgCPA = agg.revenueApptap / agg.conversionsCredited;
      } else {
        agg.avgCPA = null;
      }

      if (agg.clicksTracked > 0) {
        agg.eCPC = agg.revenueApptap / agg.clicksTracked;
      } else {
        agg.eCPC = null;
      }

      // Calculate demand-specific percentage fields
      if (agg.clicksTracked > 0) {
        agg.wastedClickPcnt = agg.wastedClicks / agg.clicksTracked;
      } else {
        agg.wastedClickPcnt = null;
      }

      if (agg.distinctProducts > 0) {
        agg.convertingProductsPcnt = agg.workingDistinctProducts / agg.distinctProducts;
      } else {
        agg.convertingProductsPcnt = null;
      }

      if (agg.distinctCampaigns > 0) {
        agg.convertingCampaignsPcnt = agg.workingDistinctCampaigns / agg.distinctCampaigns;
      } else {
        agg.convertingCampaignsPcnt = null;
      }
    });

    return Array.from(aggregatedByDate.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [rawData, dateColumnKey, sortedData, viewMode]);

  // Create pivoted summary row
  const summaryRow = useMemo(() => {
    const summaryLabel = viewMode === 'demand' ? 'All Demand Sources' : 'All Traffic Sources';
    const row: any = {
      apptapPublisherId: 'ALL',
      apptapSiteId: 'ALL',
      campaignSource: 'ALL',
      apptapPublisherName: summaryLabel,
    };

    // Pivot the summary data
    dateColumns.forEach((date: string) => {
      const dayData = summaryData.find((d: any) => d.date === date);
      row[date] = dayData ? dayData.revenueApptap : null;
    });

    return row;
  }, [summaryData, dateColumns, viewMode]);

  // Toggle row expansion
  const toggleRow = (rowKey: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column && sortDirection !== null) {
      // Toggle between asc and desc
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort indicator - always reserves space to prevent column width changes
  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) {
      return <span style={{ visibility: 'hidden' }}> ▲</span>;
    }
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Format date header (keep YYYY-MM-DD format)
  const formatDateHeader = (date: string): string => {
    return date;
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        No data available
      </div>
    );
  }

  // Calculate actual number of periods included in average and determine suffix
  // Exclude today if it's the most recent date (only for daily view)
  const today = new Date().toISOString().split('T')[0];
  const mostRecentDate = dateColumns[0];

  // Determine the period suffix based on dateColumnKey
  let periodSuffix = 'd'; // days
  if (dateColumnKey === 'month') {
    periodSuffix = 'm';
  } else if (dateColumnKey === 'week') {
    periodSuffix = 'w';
  }

  // Exclude the most recent period if it's incomplete (current day/week/month)
  let shouldExcludeMostRecent = false;
  if (dateColumnKey === 'date') {
    // Exclude today for daily view
    shouldExcludeMostRecent = mostRecentDate === today;
  } else if (dateColumnKey === 'week') {
    // Exclude current week for weekly view (format: YYYY-WNN)
    const currentYear = dayjs().year();
    const currentWeek = dayjs().isoWeek();
    const currentWeekStr = `${currentYear}-W${String(currentWeek).padStart(2, '0')}`;
    shouldExcludeMostRecent = mostRecentDate === currentWeekStr;
  } else if (dateColumnKey === 'month') {
    // Exclude current month for monthly view (format: YYYY-MM)
    const currentMonth = dayjs().format('YYYY-MM');
    shouldExcludeMostRecent = mostRecentDate === currentMonth;
  }
  const avgPeriodsCount = shouldExcludeMostRecent ? dateColumns.length - 1 : dateColumns.length;

  // Hide average column if there's only 1 or fewer periods to average
  const showAverageColumn = dateColumns.length > 1;

  // Calculate dynamic minWidth based on number of date columns
  // Base width (entity + avg columns) + date columns width
  const baseWidth = 500; // Fixed columns (expand, entity name, average)
  const dateColumnWidth = 120; // Approximate width per date column
  const dynamicMinWidth = Math.max(800, baseWidth + (dateColumns.length * dateColumnWidth));

  // Dynamic period label for tooltips
  const periodLabel = dateColumnKey === 'month' ? 'Months' : dateColumnKey === 'week' ? 'Weeks' : 'Days';

  return (
    <div style={{
      overflowX: 'auto',
      width: '100%',
      border: '1px solid #dee2e6',
      borderRadius: '4px'
    }}>
      <Table size="sm" style={{ marginBottom: 0, minWidth: `${dynamicMinWidth}px`, width: 'max-content' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
          <tr>
            {/* Expand/collapse column with legend info icon */}
            <th style={{ width: '30px', textAlign: 'center' }}>
              <InfoIcon id="statisticalHighlightingLegendTooltip">
                <div style={{ textAlign: 'left' }}>
                  <strong>Statistical Highlighting Legend:</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                    <li>↑ Increase or ↓ Decrease of 20% or more from prior day</li>
                    <li>⇈ Increase or ⇊ Decrease from daily average greater than the standard deviation</li>
                  </ul>
                </div>
              </InfoIcon>
            </th>

            {/* Entity column (Traffic Source or Demand Source) */}
            <th
              onClick={() => handleSort('apptapPublisherName')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {viewMode === 'demand' ? 'Demand Source' : 'Traffic Source'}{getSortIndicator('apptapPublisherName')}
            </th>

            {/* Average column - only show if there are multiple periods to average */}
            {showAverageColumn && (
              <th
                onClick={() => handleSort('_average')}
                style={{
                  textAlign: 'right',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
                title={
                  (() => {
                    // Generate tooltip based on period type, matching old Python dashboard style
                    let tooltip = '';
                    if (dateColumnKey === 'month') {
                      tooltip = shouldExcludeMostRecent
                        ? `Average monthly value for the past ${avgPeriodsCount} months, excluding this month.`
                        : `Average monthly value for the past ${avgPeriodsCount} months.`;
                    } else if (dateColumnKey === 'week') {
                      tooltip = shouldExcludeMostRecent
                        ? `Average weekly value for this data, excluding this week. Based on the number of weeks shown on this page.`
                        : `Average weekly value for this data. Based on the number of weeks shown on this page.`;
                    } else {
                      // Daily
                      tooltip = shouldExcludeMostRecent
                        ? `Average daily value for this data, excluding today. Based on the number of days shown on this page.`
                        : `Average daily value for this data. Based on the number of days shown on this page.`;
                    }
                    return avgColumnTooltipSuffix ? `${tooltip} ${avgColumnTooltipSuffix}` : tooltip;
                  })()
                }
              >
                {avgPeriodsCount}{periodSuffix} Avg.{getSortIndicator('_average')}
              </th>
            )}

            {/* Date columns */}
            {dateColumns.map((date: string) => (
              <th
                key={date}
                onClick={() => handleSort(date)}
                style={{
                  textAlign: 'right',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                {formatDateHeader(date)}{getSortIndicator(date)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Summary Row - All Traffic Sources / All Demand Sources */}
          {(() => {
            const rowKey = 'ALL-ALL';

            // Calculate average from summaryData to match expanded Gross Revenue row calculation
            // This ensures the summary row average matches the expanded row average
            const avgValue = (() => {
              let sum = 0;
              let count = 0;

              summaryData.forEach((dayData: any, index: number) => {
                // Skip current period's data if it's the most recent (incomplete data for current day/week/month)
                // Check by date value, not by index, in case the most recent date is missing from summaryData
                if (shouldExcludeMostRecent && dayData.date === mostRecentDate) {
                  return;
                }

                const value = dayData.revenueApptap;
                // Exclude null/undefined/empty string/zero (matching old dashboard behavior)
                if (value !== null && value !== undefined && value !== '' && value !== 0) {
                  const num = typeof value === 'string' ? parseFloat(value) : value;
                  if (!isNaN(num) && num !== 0) {
                    sum += num;
                    count++;
                  }
                }
              });

              return count > 0 ? sum / count : null;
            })();

            const isExpanded = expandedRows.has(rowKey);

            // Calculate statistical highlighting for summary row revenue
            // Exclude most recent date from statistical calculations
            const excludeDate = dateColumns[0]; // Most recent date (first in descending order)
            const historicalValues: number[] = [];
            dateColumns.forEach((date: string) => {
              if (date !== excludeDate) {
                const val = summaryRow[date];
                // Exclude null/undefined/empty/zero values (matching old dashboard behavior)
                if (val !== null && val !== undefined && val !== '' && val !== 0) {
                  const num = typeof val === 'string' ? parseFloat(val) : val;
                  if (!isNaN(num) && num !== 0) {
                    historicalValues.push(num);
                  }
                }
              }
            });

            const summaryStdDev = calculateStandardDeviation(historicalValues);
            const summaryDailyAvg = calculateMean(historicalValues);
            const summaryMin = historicalValues.length > 0 ? Math.min(...historicalValues) : 0;
            const summaryMax = historicalValues.length > 0 ? Math.max(...historicalValues) : 0;

            // Create tooltip for summary row average column
            const summaryExpectedMin = Math.max(summaryDailyAvg - summaryStdDev, 0);
            const summaryExpectedMax = summaryDailyAvg + summaryStdDev;
            const summaryAvgTooltip = `Expected (stdev) Range:\n${formatCurrency(summaryExpectedMin)} - ${formatCurrency(summaryExpectedMax)}\nHistorical Range:\n${formatCurrency(summaryMin)} - ${formatCurrency(summaryMax)}\n${periodLabel} with data: ${historicalValues.length}`;

            // Function to get highlighting classes for summary row's revenue cells
            const getSummaryHighlightingClasses = (currentDate: string, dateIndex: number): string => {
              const currentValue = summaryRow[currentDate];

              if (!currentValue || currentValue === 0) return '';

              const classes: string[] = [];

              // Get previous day value for day-over-day comparison
              if (dateIndex < dateColumns.length - 1) {
                const previousDate = dateColumns[dateIndex + 1];
                const previousValue = summaryRow[previousDate];

                if (previousValue && previousValue !== 0) {
                  const deltaYdayPcnt = (currentValue - previousValue) / previousValue;

                  // Single arrow: 20%+ change from prior day
                  if (deltaYdayPcnt > 0.2) {
                    classes.push('increase');
                  } else if (deltaYdayPcnt < -0.2) {
                    classes.push('decrease');
                  }
                }
              }

              // Double arrow: beyond 1 standard deviation from average
              if (summaryStdDev !== 0 && summaryDailyAvg !== null) {
                const deltaAvgVal = currentValue - summaryDailyAvg;

                if (deltaAvgVal > summaryStdDev) {
                  classes.push('increase-big');
                } else if (deltaAvgVal < -1 * summaryStdDev) {
                  classes.push('decrease-big');
                }
              }

              // Add field name class for field-specific styling
              if (classes.length > 0) {
                classes.push('revenueApptap');
              }

              return classes.join(' ');
            };

            return (
              <React.Fragment key={rowKey}>
                {/* Main summary row */}
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                  {/* Expand/collapse button */}
                  <td
                    onClick={() => toggleRow(rowKey)}
                    style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {isExpanded ? '−' : '+'}
                  </td>

                  {/* Entity name cell */}
                  <td
                    onClick={() => toggleRow(rowKey)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    {viewMode === 'demand' ? 'All Demand Sources' : 'All Traffic Sources'}
                  </td>

                  {/* Average cell with tooltip - only show if there are multiple periods */}
                  {showAverageColumn && (
                    <td
                      style={{ textAlign: 'right', backgroundColor: '#bbdefb', fontWeight: 'bold' }}
                      title={summaryAvgTooltip}
                    >
                      {(avgValue !== null && avgValue !== 0) ? formatCurrency(avgValue) : '-'}
                    </td>
                  )}

                  {/* Date value cells with highlighting */}
                  {dateColumns.map((date: string, dateIdx: number) => {
                    const highlightClasses = getSummaryHighlightingClasses(date, dateIdx);
                    const value = summaryRow[date];
                    // Show '-' for null/undefined/empty string/zero (matching old dashboard behavior)
                    const displayValue = (value === null || value === undefined || value === '' || value === 0) ? null : value;

                    return (
                      <td
                        key={date}
                        className={highlightClasses}
                        style={{ textAlign: 'right' }}
                        title={displayValue !== null ? `Gross Revenue: ${formatCurrency(displayValue)}` : 'No data'}
                      >
                        {displayValue !== null ? formatCurrency(displayValue) : '-'}
                      </td>
                    );
                  })}
                </tr>

                {/* Expanded summary metrics */}
                {isExpanded && (() => {
                  // Create a map of date -> data for easy lookup (needed for revenue change calculation)
                  const dataByDate = new Map();
                  summaryData.forEach((dayRow: any) => {
                    dataByDate.set(dayRow.date, dayRow);
                  });

                  // Calculate revenue change for a specific date
                  const calculateRevenueChange = (currentDate: string, dateIndex: number) => {
                    if (dateIndex >= dateColumns.length - 1) return null; // No previous date

                    const currentData = dataByDate.get(currentDate);
                    const previousDate = dateColumns[dateIndex + 1]; // Next in array is previous in time (descending order)
                    const previousData = dataByDate.get(previousDate);

                    if (!currentData || !previousData) {
                      return null;
                    }

                    const currentRevenue = currentData.revenueApptap;
                    const previousRevenue = previousData.revenueApptap;

                    // Both values must be defined (not null/undefined)
                    if (currentRevenue === null || currentRevenue === undefined || previousRevenue === null || previousRevenue === undefined) {
                      return null;
                    }

                    // If both are zero, return null (show dash, not 0%)
                    if (currentRevenue === 0 && previousRevenue === 0) {
                      return null;
                    }

                    // If previous is zero (but current is not), can't calculate percentage change (would be division by zero)
                    if (previousRevenue === 0) {
                      return null;
                    }

                    // Allow current to be zero - this represents a -100% change which is valid

                    const change = ((currentRevenue - previousRevenue) / previousRevenue);
                    return change;
                  };

                  // Calculate average revenue change across all dates
                  const calculateAverageRevenueChange = () => {
                    let sum = 0;
                    let count = 0;

                    dateColumns.forEach((date: string, idx: number) => {
                      // Skip if this is the most recent date and we're excluding it
                      if (shouldExcludeMostRecent && idx === 0) {
                        return;
                      }

                      const change = calculateRevenueChange(date, idx);
                      if (change !== null && change !== undefined) {
                        sum += change;
                        count++;
                      }
                    });

                    return count > 0 ? sum / count : null;
                  };

                  // Helper function to calculate average for additive metrics (revenue, clicks, conversions, etc.)
                  // For additive metrics shown in the average column, we sum across all days and divide by count
                  // Excludes zeros to match old dashboard behavior (zeros = no data)
                  const calculateSummaryMetricSum = (field: string) => {
                    let sum = 0;
                    let count = 0;

                    summaryData.forEach((dayData: any, index: number) => {
                      // Skip current period's data if it's the most recent (incomplete data for current day/week/month)
                      // Check by date value, not by index, in case the most recent date is missing from summaryData
                      if (shouldExcludeMostRecent && dayData.date === mostRecentDate) {
                        return;
                      }

                      const value = dayData[field];
                      // Exclude null/undefined/empty string/zero (matching old dashboard behavior)
                      if (value !== null && value !== undefined && value !== '' && value !== 0) {
                        const num = typeof value === 'string' ? parseFloat(value) : value;
                        if (!isNaN(num) && num !== 0) {
                          sum += num;
                          count++;
                        }
                      }
                    });

                    return count > 0 ? sum / count : null;
                  };

                  // Helper function to calculate average for non-additive metrics (kept for backward compatibility)
                  const calculateSummaryMetricAverage = (field: string) => {
                    let sum = 0;
                    let count = 0;

                    // Get today's date in YYYY-MM-DD format
                    const today = new Date().toISOString().split('T')[0];
                    const mostRecentDate = dateColumns[0];
                    const shouldExcludeMostRecent = mostRecentDate === today;

                    summaryData.forEach((dayData: any, index: number) => {
                      // Skip today's data if it's the most recent date
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      if (dayData[field] !== null && dayData[field] !== undefined && dayData[field] !== '') {
                        const num = typeof dayData[field] === 'string' ? parseFloat(dayData[field]) : dayData[field];
                        if (!isNaN(num)) {
                          sum += num;
                          count++;
                        }
                      }
                    });

                    if (count === 0) return null;
                    return sum / count;
                  };

                  // Helper function to calculate ratio-based averages (for percentage fields)
                  // Sums up numerator and denominator across all days (including zeros) and computes ratio
                  const calculateRatioAverage = (numeratorField: string, denominatorField: string) => {
                    let numeratorSum = 0;
                    let denominatorSum = 0;

                    const today = new Date().toISOString().split('T')[0];
                    const mostRecentDate = dateColumns[0];
                    const shouldExcludeMostRecent = mostRecentDate === today;

                    summaryData.forEach((dayData: any, index: number) => {
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      const numerator = dayData[numeratorField];
                      const denominator = dayData[denominatorField];

      const numValue = (numerator !== null && numerator !== undefined && numerator !== '')
                        ? (typeof numerator === 'string' ? parseFloat(numerator) : numerator)
                        : 0;
                      const denValue = (denominator !== null && denominator !== undefined && denominator !== '')
                        ? (typeof denominator === 'string' ? parseFloat(denominator) : denominator)
                        : 0;

                      numeratorSum += numValue;
                      denominatorSum += denValue;
                    });

                    if (denominatorSum === 0) return null;
                    return numeratorSum / denominatorSum;
                  };

                  // Helper function to calculate ratio-based averages WITH INTEGER CONVERSION
                  // Matches Python div() function which converts to int before dividing
                  // Used for CPA, CPC, and CR calculations
                  const calculateRatioAverageWithIntConversion = (numeratorField: string, denominatorField: string) => {
                    let numeratorSum = 0;
                    let denominatorSum = 0;

                    const today = new Date().toISOString().split('T')[0];
                    const mostRecentDate = dateColumns[0];
                    const shouldExcludeMostRecent = mostRecentDate === today;

                    summaryData.forEach((dayData: any, index: number) => {
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      const numerator = dayData[numeratorField];
                      const denominator = dayData[denominatorField];

                      // Apply Math.floor() to match Python's int() conversion
                      const numValue = (numerator !== null && numerator !== undefined && numerator !== '')
                        ? Math.floor(typeof numerator === 'string' ? parseFloat(numerator) : numerator)
                        : 0;
                      const denValue = (denominator !== null && denominator !== undefined && denominator !== '')
                        ? Math.floor(typeof denominator === 'string' ? parseFloat(denominator) : denominator)
                        : 0;

                      numeratorSum += numValue;
                      denominatorSum += denValue;
                    });

                    if (denominatorSum === 0) return null;
                    return numeratorSum / denominatorSum;
                  };

                  // Define the metrics to display based on view mode
                  const metrics = viewMode === 'demand' ? [
                    { label: 'Gross Revenue', field: 'revenueApptap', format: formatCurrency, avgCalculator: () => calculateSummaryMetricSum('revenueApptap') },
                    { label: 'Revenue Change', field: 'revenueChange', format: formatPercent, avgCalculator: () => calculateAverageRevenueChange() },
                    { label: 'Conversions', field: 'conversionsReceived', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('conversionsReceived') },
                    { label: 'Total Clicks', field: 'clicksTracked', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('clicksTracked') },
                    { label: 'Wasted Clicks', field: 'wastedClicks', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('wastedClicks') },
                    { label: 'Wasted Clicks %', field: 'wastedClickPcnt', format: formatPercent, avgCalculator: () => calculateRatioAverage('wastedClicks', 'clicksTracked') },
                    { label: 'Clicked Products', field: 'distinctProducts', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('distinctProducts') },
                    { label: 'Converting Products', field: 'workingDistinctProducts', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('workingDistinctProducts') },
                    { label: 'Non-Converting Products', field: 'brokenDistinctProducts', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('brokenDistinctProducts') },
                    { label: 'Converting Product %', field: 'convertingProductsPcnt', format: formatPercent, avgCalculator: () => calculateRatioAverage('workingDistinctProducts', 'distinctProducts') },
                    { label: 'Clicked Campaigns', field: 'distinctCampaigns', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('distinctCampaigns') },
                    { label: 'Converting Campaigns', field: 'workingDistinctCampaigns', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('workingDistinctCampaigns') },
                    { label: 'Non-Converting Campaigns', field: 'brokenDistinctCampaigns', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('brokenDistinctCampaigns') },
                    { label: 'Converting Campaign %', field: 'convertingCampaignsPcnt', format: formatPercent, avgCalculator: () => calculateRatioAverage('workingDistinctCampaigns', 'distinctCampaigns') },
                    { label: 'Tracked CR', field: 'averageCR', format: formatPercent, avgCalculator: () => calculateRatioAverageWithIntConversion('conversionsReceived', 'clicksTracked') },
                    { label: 'Effective CPA', field: 'avgCPA', format: formatCurrency3, avgCalculator: () => calculateRatioAverageWithIntConversion('revenueApptap', 'conversionsReceived') },
                    { label: 'Effective CPC', field: 'eCPC', format: formatCurrency3, avgCalculator: () => calculateRatioAverageWithIntConversion('revenueApptap', 'clicksTracked') },
                  ] : [
                    // Publisher view metrics (matching old dashboard - no Impressions or Effective CPC in summary)
                    { label: 'Gross Revenue', field: 'revenueApptap', format: formatCurrency, avgCalculator: () => calculateSummaryMetricSum('revenueApptap') },
                    { label: 'Revenue Change', field: 'revenueChange', format: formatPercent, avgCalculator: () => calculateAverageRevenueChange() },
                    { label: 'Publisher Payout', field: 'revenuePublisher', format: formatCurrency, avgCalculator: () => calculateSummaryMetricSum('revenuePublisher') },
                    { label: 'Reported Cost', field: 'reportedClickCost', format: formatCurrency, avgCalculator: () => calculateSummaryMetricSum('reportedClickCost') },
                    { label: 'Approx Net Revenue', field: 'apptapNetRevenue', format: formatCurrency, avgCalculator: () => calculateSummaryMetricSum('apptapNetRevenue') },
                    { label: 'Clicks', field: 'clicksReceived', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('clicksReceived') },
                    { label: 'Tracked Clicks', field: 'clicksTracked', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('clicksTracked') },
                    { label: 'Tracked %', field: 'publisherTrackedCreditRate', format: formatPercent, avgCalculator: () => calculateRatioAverage('clicksTracked', 'clicksReceived') },
                    { label: 'Conversions', field: 'conversionsReceived', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('conversionsReceived') },
                    { label: 'Credited Conversions', field: 'conversionsCredited', format: formatNumber, avgCalculator: () => calculateSummaryMetricSum('conversionsCredited') },
                    { label: 'Credited %', field: 'trackedCreditRate', format: formatPercent, avgCalculator: () => calculateRatioAverage('conversionsCredited', 'conversionsReceived') },
                    { label: 'Average CR', field: 'averageCR', format: formatPercent, avgCalculator: () => calculateRatioAverageWithIntConversion('conversionsReceived', 'clicksReceived') },
                    { label: 'Effective CPA', field: 'avgCPA', format: formatCurrency3, avgCalculator: () => calculateRatioAverageWithIntConversion('revenueApptap', 'conversionsReceived') },
                  ];

                  // Calculate statistical highlighting classes for a cell
                  const getHighlightingClasses = (field: string, currentDate: string, dateIndex: number, fieldStdDev: number, fieldDailyAvg: number): string => {
                    const currentData = dataByDate.get(currentDate);
                    let currentValue = currentData ? currentData[field] : null;

                    // For calculated fields, compute the value instead of looking it up
                    if (currentData) {
                      if (field === 'apptapNetRevenue') {
                        const revenue = currentData.revenueApptap || 0;
                        const payout = currentData.revenuePublisher || 0;
                        const cost = currentData.reportedClickCost || 0;
                        currentValue = revenue - payout - cost;
                      } else if (field === 'averageCR') {
                        const conversions = Math.floor(currentData.conversionsReceived || 0);
                        const clicks = viewMode === 'demand'
                          ? Math.floor(currentData.clicksTracked || 0)
                          : Math.floor(currentData.clicksReceived || 0);
                        currentValue = clicks !== 0 ? conversions / clicks : null;
                      } else if (field === 'avgCPA') {
                        const revenue = Math.floor(currentData.revenueApptap || 0);
                        const conversions = Math.floor(currentData.conversionsReceived || 0);
                        currentValue = conversions !== 0 ? revenue / conversions : null;
                      } else if (field === 'eCPC') {
                        const revenue = Math.floor(currentData.revenueApptap || 0);
                        const clicks = viewMode === 'demand'
                          ? Math.floor(currentData.clicksTracked || 0)
                          : Math.floor(currentData.clicksReceived || 0);
                        currentValue = clicks !== 0 ? revenue / clicks : null;
                      } else if (field === 'wastedClickPcnt') {
                        const wasted = currentData.wastedClicks || 0;
                        const tracked = currentData.clicksTracked || 0;
                        currentValue = tracked !== 0 ? wasted / tracked : null;
                      } else if (field === 'convertingProductsPcnt') {
                        const working = currentData.workingDistinctProducts || 0;
                        const total = currentData.distinctProducts || 0;
                        currentValue = total !== 0 ? working / total : null;
                      } else if (field === 'convertingCampaignsPcnt') {
                        const working = currentData.workingDistinctCampaigns || 0;
                        const total = currentData.distinctCampaigns || 0;
                        currentValue = total !== 0 ? working / total : null;
                      } else if (field === 'publisherTrackedCreditRate') {
                        const tracked = currentData.clicksTracked || 0;
                        const received = currentData.clicksReceived || 0;
                        currentValue = received !== 0 ? tracked / received : null;
                      } else if (field === 'trackedCreditRate') {
                        const credited = currentData.conversionsCredited || 0;
                        const received = currentData.conversionsReceived || 0;
                        currentValue = received !== 0 ? credited / received : null;
                      }
                    }

                    if (!currentValue || currentValue === 0) return '';

                    const classes: string[] = [];

                    // Get previous day value for day-over-day comparison
                    if (dateIndex < dateColumns.length - 1) {
                      const previousDate = dateColumns[dateIndex + 1];
                      const previousData = dataByDate.get(previousDate);
                      let previousValue = previousData ? previousData[field] : null;

                      // For calculated fields, compute the previous value
                      if (previousData) {
                        if (field === 'apptapNetRevenue') {
                          const revenue = previousData.revenueApptap || 0;
                          const payout = previousData.revenuePublisher || 0;
                          const cost = previousData.reportedClickCost || 0;
                          previousValue = revenue - payout - cost;
                        } else if (field === 'averageCR') {
                          const conversions = Math.floor(previousData.conversionsReceived || 0);
                          const clicks = viewMode === 'demand'
                            ? Math.floor(previousData.clicksTracked || 0)
                            : Math.floor(previousData.clicksReceived || 0);
                          previousValue = clicks !== 0 ? conversions / clicks : null;
                        } else if (field === 'avgCPA') {
                          const revenue = Math.floor(previousData.revenueApptap || 0);
                          const conversions = Math.floor(previousData.conversionsReceived || 0);
                          previousValue = conversions !== 0 ? revenue / conversions : null;
                        } else if (field === 'eCPC') {
                          const revenue = Math.floor(previousData.revenueApptap || 0);
                          const clicks = viewMode === 'demand'
                            ? Math.floor(previousData.clicksTracked || 0)
                            : Math.floor(previousData.clicksReceived || 0);
                          previousValue = clicks !== 0 ? revenue / clicks : null;
                        } else if (field === 'wastedClickPcnt') {
                          const wasted = previousData.wastedClicks || 0;
                          const tracked = previousData.clicksTracked || 0;
                          previousValue = tracked !== 0 ? wasted / tracked : null;
                        } else if (field === 'convertingProductsPcnt') {
                          const working = previousData.workingDistinctProducts || 0;
                          const total = previousData.distinctProducts || 0;
                          previousValue = total !== 0 ? working / total : null;
                        } else if (field === 'convertingCampaignsPcnt') {
                          const working = previousData.workingDistinctCampaigns || 0;
                          const total = previousData.distinctCampaigns || 0;
                          previousValue = total !== 0 ? working / total : null;
                        } else if (field === 'publisherTrackedCreditRate') {
                          const tracked = previousData.clicksTracked || 0;
                          const received = previousData.clicksReceived || 0;
                          previousValue = received !== 0 ? tracked / received : null;
                        } else if (field === 'trackedCreditRate') {
                          const credited = previousData.conversionsCredited || 0;
                          const received = previousData.conversionsReceived || 0;
                          previousValue = received !== 0 ? credited / received : null;
                        }
                      }

                      if (previousValue && previousValue !== 0) {
                        const deltaYdayPcnt = (currentValue - previousValue) / previousValue;

                        // Single arrow: 20%+ change from prior day
                        if (deltaYdayPcnt > 0.2) {
                          classes.push('increase');
                        } else if (deltaYdayPcnt < -0.2) {
                          classes.push('decrease');
                        }
                      }
                    }

                    // Double arrow: beyond 1 standard deviation from average
                    if (fieldStdDev !== 0 && fieldDailyAvg !== null) {
                      const deltaAvgVal = currentValue - fieldDailyAvg;

                      if (deltaAvgVal > fieldStdDev) {
                        classes.push('increase-big');
                      } else if (deltaAvgVal < -1 * fieldStdDev) {
                        classes.push('decrease-big');
                      }
                    }

                    // Add field name class for field-specific styling (e.g., revenueApptap)
                    if (classes.length > 0) {
                      classes.push(field);
                    }

                    return classes.join(' ');
                  };

                  return (
                    <>
                      {metrics.map((metric) => {
                        // Special handling for Revenue Change row
                        if (metric.label === 'Revenue Change') {
                          const avgChange = calculateAverageRevenueChange();

                          // Calculate historical values for tooltip
                          const revenueChangeValues: number[] = [];
                          dateColumns.forEach((date: string, idx: number) => {
                            // Skip if this is the most recent date and we're excluding it
                            if (shouldExcludeMostRecent && idx === 0) {
                              return;
                            }

                            const change = calculateRevenueChange(date, idx);
                            if (change !== null && change !== undefined) {
                              revenueChangeValues.push(change);
                            }
                          });

                          const changeStdDev = calculateStandardDeviation(revenueChangeValues);
                          const changeMean = calculateMean(revenueChangeValues);
                          const changeMin = revenueChangeValues.length > 0 ? Math.min(...revenueChangeValues) : 0;
                          const changeMax = revenueChangeValues.length > 0 ? Math.max(...revenueChangeValues) : 0;
                          const changeExpectedMin = Math.max(changeMean - changeStdDev, -1); // Revenue change can go below 0, but cap at -100%
                          const changeExpectedMax = changeMean + changeStdDev;
                          const changeTooltip = `Expected (stdev) Range:\n${formatPercent(changeExpectedMin)} - ${formatPercent(changeExpectedMax)}\nHistorical Range:\n${formatPercent(changeMin)} - ${formatPercent(changeMax)}\n${periodLabel} with data: ${revenueChangeValues.length}`;

                          // Function to get highlighting classes for Revenue Change cells
                          const getRevenueChangeHighlightingClasses = (currentDate: string, dateIndex: number): string => {
                            const currentChange = calculateRevenueChange(currentDate, dateIndex);

                            if (currentChange === null || currentChange === undefined) return '';

                            const classes: string[] = [];

                            // Get previous revenue change for day-over-day comparison
                            if (dateIndex < dateColumns.length - 1) {
                              const previousChange = calculateRevenueChange(dateColumns[dateIndex + 1], dateIndex + 1);

                              if (previousChange !== null && previousChange !== undefined) {
                                // For revenue change, we compare absolute differences rather than percentages
                                // e.g., if previous was -10% and current is +10%, that's a 20 percentage point increase
                                const changeDelta = currentChange - previousChange;

                                // Single arrow: 20 percentage point change from prior day
                                if (changeDelta > 0.2) {
                                  classes.push('increase');
                                } else if (changeDelta < -0.2) {
                                  classes.push('decrease');
                                }
                              }
                            }

                            // Double arrow: beyond 1 standard deviation from average
                            if (changeStdDev !== 0 && changeMean !== null) {
                              const deltaAvgVal = currentChange - changeMean;

                              if (deltaAvgVal > changeStdDev) {
                                classes.push('increase-big');
                              } else if (deltaAvgVal < -1 * changeStdDev) {
                                classes.push('decrease-big');
                              }
                            }

                            // Add field name class for field-specific styling
                            if (classes.length > 0) {
                              classes.push('revenueChange');
                            }

                            return classes.join(' ');
                          };

                          return (
                            <tr key="revenueChange" style={{ backgroundColor: '#f0f8ff', fontSize: '0.85em' }}>
                              <td></td>
                              <td style={{ paddingLeft: '30px', fontStyle: 'italic' }}>Revenue Change</td>
                              {/* Average column - show calculated average with tooltip */}
                              {showAverageColumn && (
                                <td
                                  style={{ textAlign: 'right', backgroundColor: '#e1f5fe', fontWeight: 'bold', color: '#aaa' }}
                                  title={changeTooltip}
                                >
                                  {avgChange !== null ? formatPercent(avgChange) : '-'}
                                </td>
                              )}
                              {dateColumns.map((date: string, idx: number) => {
                                const change = calculateRevenueChange(date, idx);
                                const highlightClasses = getRevenueChangeHighlightingClasses(date, idx);
                                return (
                                  <td
                                    key={date}
                                    className={highlightClasses}
                                    style={{ textAlign: 'right' }}
                                    title={change !== null ? `Revenue Change: ${formatPercent(change)}` : 'No data'}
                                  >
                                    {change !== null ? formatPercent(change) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        }

                        // Calculate standard deviation and mean for this metric (excluding most recent date)
                        const excludeDate = dateColumns[0]; // Most recent date (first in descending order)
                        const historicalValues: number[] = [];
                        dateColumns.forEach((date: string) => {
                          if (date !== excludeDate) {
                            const dayData = dataByDate.get(date);
                            let val = dayData ? dayData[metric.field] : null;

                            // For calculated fields, compute the value instead of looking it up
                            if (dayData) {
                              if (metric.field === 'apptapNetRevenue') {
                                const revenue = dayData.revenueApptap || 0;
                                const payout = dayData.revenuePublisher || 0;
                                const cost = dayData.reportedClickCost || 0;
                                val = revenue - payout - cost;
                              } else if (metric.field === 'averageCR') {
                                const conversions = Math.floor(dayData.conversionsReceived || 0);
                                const clicks = viewMode === 'demand'
                                  ? Math.floor(dayData.clicksTracked || 0)
                                  : Math.floor(dayData.clicksReceived || 0);
                                val = clicks !== 0 ? conversions / clicks : null;
                              } else if (metric.field === 'avgCPA') {
                                const revenue = Math.floor(dayData.revenueApptap || 0);
                                const conversions = Math.floor(dayData.conversionsReceived || 0);
                                val = conversions !== 0 ? revenue / conversions : null;
                              } else if (metric.field === 'eCPC') {
                                const revenue = Math.floor(dayData.revenueApptap || 0);
                                const clicks = viewMode === 'demand'
                                  ? Math.floor(dayData.clicksTracked || 0)
                                  : Math.floor(dayData.clicksReceived || 0);
                                val = clicks !== 0 ? revenue / clicks : null;
                              } else if (metric.field === 'wastedClickPcnt') {
                                const wasted = dayData.wastedClicks || 0;
                                const tracked = dayData.clicksTracked || 0;
                                val = tracked !== 0 ? wasted / tracked : null;
                              } else if (metric.field === 'convertingProductsPcnt') {
                                const working = dayData.workingDistinctProducts || 0;
                                const total = dayData.distinctProducts || 0;
                                val = total !== 0 ? working / total : null;
                              } else if (metric.field === 'convertingCampaignsPcnt') {
                                const working = dayData.workingDistinctCampaigns || 0;
                                const total = dayData.distinctCampaigns || 0;
                                val = total !== 0 ? working / total : null;
                              } else if (metric.field === 'publisherTrackedCreditRate') {
                                const tracked = dayData.clicksTracked || 0;
                                const received = dayData.clicksReceived || 0;
                                val = received !== 0 ? tracked / received : null;
                              } else if (metric.field === 'trackedCreditRate') {
                                const credited = dayData.conversionsCredited || 0;
                                const received = dayData.conversionsReceived || 0;
                                val = received !== 0 ? credited / received : null;
                              }
                            }

                            // Exclude null/undefined/empty/zero values (matching old dashboard behavior)
                            if (val !== null && val !== undefined && val !== '' && val !== 0) {
                              const num = typeof val === 'string' ? parseFloat(val) : val;
                              if (!isNaN(num) && num !== 0) {
                                historicalValues.push(num);
                              }
                            }
                          }
                        });

                        const fieldStdDev = calculateStandardDeviation(historicalValues);
                        const fieldDailyAvg = calculateMean(historicalValues);
                        const fieldMin = historicalValues.length > 0 ? Math.min(...historicalValues) : 0;
                        const fieldMax = historicalValues.length > 0 ? Math.max(...historicalValues) : 0;

                        // Regular metrics - use custom calculator if provided, otherwise use default
                        const avgValue = metric.avgCalculator ? metric.avgCalculator() : calculateSummaryMetricAverage(metric.field);

                        // Create tooltip content
                        const expectedMin = Math.max(fieldDailyAvg - fieldStdDev, 0);
                        const expectedMax = fieldDailyAvg + fieldStdDev;
                        const tooltipContent = `Expected (stdev) Range:\n${metric.format(expectedMin)} - ${metric.format(expectedMax)}\nHistorical Range:\n${metric.format(fieldMin)} - ${metric.format(fieldMax)}\n${periodLabel} with data: ${historicalValues.length}`;

                        return (
                          <tr key={metric.field} style={{ backgroundColor: '#f0f8ff', fontSize: '0.85em' }}>
                            {/* Empty cell for expand button column */}
                            <td></td>

                            {/* Metric name cell */}
                            <td style={{ paddingLeft: '30px', fontStyle: 'italic' }}>
                              {metric.label}
                            </td>

                            {/* 10d Average cell with tooltip - only show if there are multiple periods */}
                            {showAverageColumn && (
                              <td
                                style={{ textAlign: 'right', backgroundColor: '#e1f5fe', fontWeight: 'bold', color: '#aaa' }}
                                title={tooltipContent}
                              >
                                {(avgValue !== null && avgValue !== 0) ? metric.format(avgValue) : '-'}
                              </td>
                            )}

                            {/* Date value cells with highlighting */}
                            {dateColumns.map((date: string, idx: number) => {
                              const dayData = dataByDate.get(date);
                              let value = dayData ? dayData[metric.field] : null;

                              // Special calculations to match Python dashboard (utils/__init__.py)
                              // These calculations use the div() function which converts to int before dividing
                              if (dayData) {
                                // Approx Net Revenue: revenueApptap - revenuePublisher - reportedClickCost
                                if (metric.field === 'apptapNetRevenue') {
                                  const revenue = dayData.revenueApptap || 0;
                                  const payout = dayData.revenuePublisher || 0;
                                  const cost = dayData.reportedClickCost || 0;
                                  value = revenue - payout - cost;
                                }
                                // Average CR: conversionsReceived / clicks
                                // Demand: conversionsReceived / clicksTracked
                                // Publisher: conversionsReceived / clicksReceived
                                // NOTE: Python div() converts to int before dividing
                                else if (metric.field === 'averageCR') {
                                  const conversions = Math.floor(dayData.conversionsReceived || 0);
                                  const clicks = viewMode === 'demand'
                                    ? Math.floor(dayData.clicksTracked || 0)
                                    : Math.floor(dayData.clicksReceived || 0);
                                  value = clicks !== 0 ? conversions / clicks : null;
                                }
                                // Effective CPA: revenueApptap / conversionsReceived
                                // NOTE: Python div() converts to int before dividing (int(3.85) = 3)
                                else if (metric.field === 'avgCPA') {
                                  const revenue = Math.floor(dayData.revenueApptap || 0);
                                  const conversions = Math.floor(dayData.conversionsReceived || 0);
                                  value = conversions !== 0 ? revenue / conversions : null;
                                }
                                // Effective CPC: revenueApptap / clicksReceived (or clicksTracked for demand)
                                // NOTE: Python div() converts to int before dividing
                                else if (metric.field === 'eCPC') {
                                  const revenue = Math.floor(dayData.revenueApptap || 0);
                                  const clicks = viewMode === 'demand'
                                    ? Math.floor(dayData.clicksTracked || 0)
                                    : Math.floor(dayData.clicksReceived || 0);
                                  value = clicks !== 0 ? revenue / clicks : null;
                                }
                              }

                              // Show '-' for null/undefined/empty string/zero (matching old dashboard behavior)
                              const displayValue = (value === null || value === undefined || value === '' || value === 0) ? null : value;
                              const highlightClasses = getHighlightingClasses(metric.field, date, idx, fieldStdDev, fieldDailyAvg);

                              return (
                                <td
                                  key={date}
                                  className={highlightClasses}
                                  style={{ textAlign: 'right' }}
                                  title={displayValue !== null ? `${metric.label}: ${metric.format(displayValue)}` : 'No data'}
                                >
                                  {displayValue !== null ? metric.format(displayValue) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </>
                  );
                })()}
              </React.Fragment>
            );
          })()}

          {/* Individual Entity Rows (Traffic Sources or Demand Sources) */}
          {sortedData.map((row, idx) => {
            // Create a unique key based on view mode
            const rowKey = viewMode === 'demand'
              ? row.campaignSource || `row-${idx}`
              : `${row.apptapPublisherId}-${row.apptapSiteId}` || `row-${idx}`;
            const avgValue = calculateRowAverage(row, dateColumns);
            const isExpanded = expandedRows.has(rowKey);

            // Format the entity name based on view mode
            const displayName = viewMode === 'demand'
              ? PagesHelper.getDemandSourceLabel(row.campaignSource, false)
              : PagesHelper.getTrafficSourceLabel(row.apptapPublisherId, row.apptapSiteId, trafficSources);

            // Calculate statistical highlighting for this publisher's revenue
            // Exclude most recent date from statistical calculations
            const excludeDate = dateColumns[0]; // Most recent date (first in descending order)
            const historicalValues: number[] = [];
            dateColumns.forEach((date: string) => {
              if (date !== excludeDate) {
                const val = row[date];
                if (val !== null && val !== undefined && val !== '') {
                  const num = typeof val === 'string' ? parseFloat(val) : val;
                  if (!isNaN(num)) {
                    historicalValues.push(num);
                  }
                }
              }
            });

            const rowStdDev = calculateStandardDeviation(historicalValues);
            const rowDailyAvg = calculateMean(historicalValues);
            const rowMin = historicalValues.length > 0 ? Math.min(...historicalValues) : 0;
            const rowMax = historicalValues.length > 0 ? Math.max(...historicalValues) : 0;

            // Create tooltip for average column
            const expectedMin = Math.max(rowDailyAvg - rowStdDev, 0);
            const expectedMax = rowDailyAvg + rowStdDev;
            const avgTooltip = `Expected (stdev) Range:\n${formatCurrency(expectedMin)} - ${formatCurrency(expectedMax)}\nHistorical Range:\n${formatCurrency(rowMin)} - ${formatCurrency(rowMax)}\n${periodLabel} with data: ${historicalValues.length}`;

            // Function to get highlighting classes for this publisher's revenue cells
            const getRowHighlightingClasses = (currentDate: string, dateIndex: number): string => {
              const currentValue = row[currentDate];

              if (!currentValue || currentValue === 0) return '';

              const classes: string[] = [];

              // Get previous day value for day-over-day comparison
              if (dateIndex < dateColumns.length - 1) {
                const previousDate = dateColumns[dateIndex + 1];
                const previousValue = row[previousDate];

                if (previousValue && previousValue !== 0) {
                  const deltaYdayPcnt = (currentValue - previousValue) / previousValue;

                  // Single arrow: 20%+ change from prior day
                  if (deltaYdayPcnt > 0.2) {
                    classes.push('increase');
                  } else if (deltaYdayPcnt < -0.2) {
                    classes.push('decrease');
                  }
                }
              }

              // Double arrow: beyond 1 standard deviation from average
              if (rowStdDev !== 0 && rowDailyAvg !== null) {
                const deltaAvgVal = currentValue - rowDailyAvg;

                if (deltaAvgVal > rowStdDev) {
                  classes.push('increase-big');
                } else if (deltaAvgVal < -1 * rowStdDev) {
                  classes.push('decrease-big');
                }
              }

              // Add field name class for field-specific styling
              if (classes.length > 0) {
                classes.push('revenueApptap');
              }

              return classes.join(' ');
            };

            return (
              <React.Fragment key={rowKey}>
                {/* Main row */}
                <tr>
                  {/* Expand/collapse button */}
                  <td
                    onClick={() => toggleRow(rowKey)}
                    style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {isExpanded ? '−' : '+'}
                  </td>

                  {/* Entity name cell */}
                  <td
                    onClick={() => toggleRow(rowKey)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title={viewMode === 'demand'
                      ? `Campaign Source: ${row.campaignSource || '-'}`
                      : `Publisher ID: ${row.apptapPublisherId || '-'} | Site ID: ${row.apptapSiteId || '-'}`
                    }
                  >
                    {displayName}
                  </td>

                  {/* 10d Average cell with tooltip - only show if there are multiple periods */}
                  {showAverageColumn && (
                    <td
                      style={{ textAlign: 'right', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}
                      title={avgTooltip}
                    >
                      {avgValue !== null ? formatCurrency(avgValue) : '-'}
                    </td>
                  )}

                  {/* Date value cells with highlighting */}
                  {dateColumns.map((date: string, dateIdx: number) => {
                    const highlightClasses = getRowHighlightingClasses(date, dateIdx);
                    const value = row[date];

                    return (
                      <td
                        key={date}
                        className={highlightClasses}
                        style={{ textAlign: 'right' }}
                        title={value !== null && value !== undefined && value !== '' ? `Gross Revenue: ${formatCurrency(value)}` : 'No data'}
                      >
                        {formatCurrency(row[date])}
                      </td>
                    );
                  })}
                </tr>

                {/* Expanded row - show all metrics with dates as columns */}
                {isExpanded && (() => {
                  const dailyData = viewMode === 'demand'
                    ? getRawDataForEntity(row.campaignSource)
                    : getRawDataForEntity(row.apptapPublisherId, row.apptapSiteId);

                  // Create a map of date/week/month -> data for easy lookup
                  const dataByDate = new Map();
                  dailyData.forEach((dayRow: any) => {
                    const periodValue = dayRow[dateColumnKey]; // Use the appropriate date field
                    dataByDate.set(periodValue, dayRow);
                  });

                  // Helper function to calculate average for additive metrics (revenue, clicks, conversions, etc.)
                  // Returns the average across days with data (excluding zeros and today if applicable)
                  const calculateMetricSum = (field: string) => {
                    let sum = 0;
                    let count = 0;

                    dateColumns.forEach((date: string, index: number) => {
                      // Skip current period's data if it's the most recent (incomplete data for current day/week/month)
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      const dayData = dataByDate.get(date);
                      if (dayData && dayData[field] !== null && dayData[field] !== undefined && dayData[field] !== '') {
                        const num = typeof dayData[field] === 'string' ? parseFloat(dayData[field]) : dayData[field];
                        // Exclude zeros when calculating average (matching old dashboard behavior)
                        if (!isNaN(num) && num !== 0) {
                          sum += num;
                          count++;
                        }
                      }
                    });

                    return count > 0 ? sum / count : null;
                  };

                  // Helper function to calculate ratio-based metrics (for percentages)
                  const calculateMetricRatio = (numeratorField: string, denominatorField: string) => {
                    let numeratorSum = 0;
                    let denominatorSum = 0;

                    const today = new Date().toISOString().split('T')[0];
                    const mostRecentDate = dateColumns[0];
                    const shouldExcludeMostRecent = mostRecentDate === today;

                    dateColumns.forEach((date: string, index: number) => {
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      const dayData = dataByDate.get(date);
                      if (dayData) {
                        const numValue = (dayData[numeratorField] !== null && dayData[numeratorField] !== undefined && dayData[numeratorField] !== '')
                          ? (typeof dayData[numeratorField] === 'string' ? parseFloat(dayData[numeratorField]) : dayData[numeratorField])
                          : 0;
                        const denValue = (dayData[denominatorField] !== null && dayData[denominatorField] !== undefined && dayData[denominatorField] !== '')
                          ? (typeof dayData[denominatorField] === 'string' ? parseFloat(dayData[denominatorField]) : dayData[denominatorField])
                          : 0;

                        numeratorSum += numValue;
                        denominatorSum += denValue;
                      }
                    });

                    if (denominatorSum === 0) return null;
                    return numeratorSum / denominatorSum;
                  };

                  // Helper function to calculate ratio-based metrics WITH INTEGER CONVERSION
                  // Matches Python div() function which converts to int before dividing
                  // Used for CPA, CPC, and CR calculations
                  const calculateMetricRatioWithIntConversion = (numeratorField: string, denominatorField: string) => {
                    let numeratorSum = 0;
                    let denominatorSum = 0;

                    const today = new Date().toISOString().split('T')[0];
                    const mostRecentDate = dateColumns[0];
                    const shouldExcludeMostRecent = mostRecentDate === today;

                    dateColumns.forEach((date: string, index: number) => {
                      if (shouldExcludeMostRecent && index === 0) {
                        return;
                      }

                      const dayData = dataByDate.get(date);
                      if (dayData) {
                        // Apply Math.floor() to match Python's int() conversion
                        const numValue = (dayData[numeratorField] !== null && dayData[numeratorField] !== undefined && dayData[numeratorField] !== '')
                          ? Math.floor(typeof dayData[numeratorField] === 'string' ? parseFloat(dayData[numeratorField]) : dayData[numeratorField])
                          : 0;
                        const denValue = (dayData[denominatorField] !== null && dayData[denominatorField] !== undefined && dayData[denominatorField] !== '')
                          ? Math.floor(typeof dayData[denominatorField] === 'string' ? parseFloat(dayData[denominatorField]) : dayData[denominatorField])
                          : 0;

                        numeratorSum += numValue;
                        denominatorSum += denValue;
                      }
                    });

                    if (denominatorSum === 0) return null;
                    return numeratorSum / denominatorSum;
                  };

                  // Helper function to calculate revenue change for individual row
                  // Matches Python dashboard logic from utils/__init__.py:73-81
                  const calculateRowRevenueChange = (currentDate: string, dateIndex: number) => {
                    if (dateIndex >= dateColumns.length - 1) return null; // No previous date

                    const currentData = dataByDate.get(currentDate);
                    const previousDate = dateColumns[dateIndex + 1]; // Next in array is previous in time (descending order)
                    const previousData = dataByDate.get(previousDate);

                    if (!currentData || !previousData) {
                      return null;
                    }

                    const currentRevenue = currentData.revenueApptap;
                    const previousRevenue = previousData.revenueApptap;

                    // Both values must be defined (not null/undefined)
                    if (currentRevenue === null || currentRevenue === undefined || previousRevenue === null || previousRevenue === undefined) {
                      return null;
                    }

                    // If both are zero, return null (show dash, not 0%)
                    if (currentRevenue === 0 && previousRevenue === 0) {
                      return null;
                    }

                    // If previous is zero (but current is not), can't calculate change
                    if (previousRevenue === 0) {
                      return null;
                    }

                    const change = (currentRevenue - previousRevenue) / previousRevenue;
                    return change;
                  };

                  // Define the metrics to display based on view mode
                  const metrics = viewMode === 'demand' ? [
                    // Demand view metrics - specify calculator for each metric
                    { label: 'Gross Revenue', field: 'revenueApptap', format: formatCurrency, calculator: () => calculateMetricSum('revenueApptap') },
                    { label: 'Revenue Change', field: 'revenueChange', format: formatPercent, calculator: null }, // Special handling below
                    { label: 'Conversions', field: 'conversionsReceived', format: formatNumber, calculator: () => calculateMetricSum('conversionsReceived') },
                    { label: 'Total Clicks', field: 'clicksTracked', format: formatNumber, calculator: () => calculateMetricSum('clicksTracked') },
                    { label: 'Wasted Clicks', field: 'wastedClicks', format: formatNumber, calculator: () => calculateMetricSum('wastedClicks') },
                    { label: 'Wasted Clicks %', field: 'wastedClickPcnt', format: formatPercent, calculator: () => calculateMetricRatio('wastedClicks', 'clicksTracked') },
                    { label: 'Clicked Products', field: 'distinctProducts', format: formatNumber, calculator: () => calculateMetricSum('distinctProducts') },
                    { label: 'Converting Products', field: 'workingDistinctProducts', format: formatNumber, calculator: () => calculateMetricSum('workingDistinctProducts') },
                    { label: 'Non-Converting Products', field: 'brokenDistinctProducts', format: formatNumber, calculator: () => calculateMetricSum('brokenDistinctProducts') },
                    { label: 'Converting Product %', field: 'convertingProductsPcnt', format: formatPercent, calculator: () => calculateMetricRatio('workingDistinctProducts', 'distinctProducts') },
                    { label: 'Clicked Campaigns', field: 'distinctCampaigns', format: formatNumber, calculator: () => calculateMetricSum('distinctCampaigns') },
                    { label: 'Converting Campaigns', field: 'workingDistinctCampaigns', format: formatNumber, calculator: () => calculateMetricSum('workingDistinctCampaigns') },
                    { label: 'Non-Converting Campaigns', field: 'brokenDistinctCampaigns', format: formatNumber, calculator: () => calculateMetricSum('brokenDistinctCampaigns') },
                    { label: 'Converting Campaign %', field: 'convertingCampaignsPcnt', format: formatPercent, calculator: () => calculateMetricRatio('workingDistinctCampaigns', 'distinctCampaigns') },
                    { label: 'Tracked CR', field: 'averageCR', format: formatPercent, calculator: () => calculateMetricRatioWithIntConversion('conversionsReceived', 'clicksTracked') },
                    { label: 'Effective CPA', field: 'avgCPA', format: formatCurrency3, calculator: () => calculateMetricRatioWithIntConversion('revenueApptap', 'conversionsReceived') },
                    { label: 'Effective CPC', field: 'eCPC', format: formatCurrency3, calculator: () => calculateMetricRatioWithIntConversion('revenueApptap', 'clicksTracked') },
                  ] : [
                    // Publisher view metrics - specify calculator for each metric (matches Python publishers.py lines 15-47)
                    { label: 'Gross Revenue', field: 'revenueApptap', format: formatCurrency, calculator: () => calculateMetricSum('revenueApptap') },
                    { label: 'Revenue Change', field: 'revenueChange', format: formatPercent, calculator: null }, // Special handling below
                    { label: 'Publisher Payout', field: 'revenuePublisher', format: formatCurrency, calculator: () => calculateMetricSum('revenuePublisher') },
                    { label: 'Reported Cost', field: 'reportedClickCost', format: formatCurrency, calculator: () => calculateMetricSum('reportedClickCost') },
                    { label: 'Approx Net Revenue', field: 'apptapNetRevenue', format: formatCurrency, calculator: () => calculateMetricSum('apptapNetRevenue') },
                    { label: 'Impressions', field: 'Impressions', format: formatNumber, calculator: () => calculateMetricSum('Impressions') },
                    { label: 'Clicks', field: 'clicksReceived', format: formatNumber, calculator: () => calculateMetricSum('clicksReceived') },
                    { label: 'Tracked Clicks', field: 'clicksTracked', format: formatNumber, calculator: () => calculateMetricSum('clicksTracked') },
                    { label: 'Tracked %', field: 'publisherTrackedCreditRate', format: formatPercent, calculator: () => calculateMetricRatio('clicksTracked', 'clicksReceived') },
                    { label: 'Conversions', field: 'conversionsReceived', format: formatNumber, calculator: () => calculateMetricSum('conversionsReceived') },
                    { label: 'Credited Conversions', field: 'conversionsCredited', format: formatNumber, calculator: () => calculateMetricSum('conversionsCredited') },
                    { label: 'Credited %', field: 'trackedCreditRate', format: formatPercent, calculator: () => calculateMetricRatio('conversionsCredited', 'conversionsReceived') },
                    { label: 'Average CR', field: 'averageCR', format: formatPercent, calculator: () => calculateMetricRatioWithIntConversion('conversionsReceived', 'clicksReceived') },
                    { label: 'Effective CPA', field: 'avgCPA', format: formatCurrency3, calculator: () => calculateMetricRatioWithIntConversion('revenueApptap', 'conversionsReceived') },
                    { label: 'Effective CPC', field: 'eCPC', format: formatCurrency3, calculator: () => calculateMetricRatioWithIntConversion('revenueApptap', 'clicksReceived') },
                  ];

                  // Calculate revenue change historical values for this entity (for highlighting)
                  const entityRevenueChangeValues: number[] = [];
                  dateColumns.forEach((date: string, idx: number) => {
                    if (shouldExcludeMostRecent && idx === 0) {
                      return;
                    }
                    const change = calculateRowRevenueChange(date, idx);
                    if (change !== null && change !== undefined) {
                      entityRevenueChangeValues.push(change);
                    }
                  });

                  const entityChangeStdDev = calculateStandardDeviation(entityRevenueChangeValues);
                  const entityChangeMean = calculateMean(entityRevenueChangeValues);

                  // Function to get highlighting classes for entity Revenue Change cells
                  const getEntityRevenueChangeHighlightingClasses = (currentDate: string, dateIndex: number): string => {
                    const currentChange = calculateRowRevenueChange(currentDate, dateIndex);

                    if (currentChange === null || currentChange === undefined) return '';

                    const classes: string[] = [];

                    // Get previous revenue change for day-over-day comparison
                    if (dateIndex < dateColumns.length - 1) {
                      const previousChange = calculateRowRevenueChange(dateColumns[dateIndex + 1], dateIndex + 1);

                      if (previousChange !== null && previousChange !== undefined) {
                        // For revenue change, we compare absolute differences rather than percentages
                        const changeDelta = currentChange - previousChange;

                        // Single arrow: 20 percentage point change from prior day
                        if (changeDelta > 0.2) {
                          classes.push('increase');
                        } else if (changeDelta < -0.2) {
                          classes.push('decrease');
                        }
                      }
                    }

                    // Double arrow: beyond 1 standard deviation from average
                    if (entityChangeStdDev !== 0 && entityChangeMean !== null) {
                      const deltaAvgVal = currentChange - entityChangeMean;

                      if (deltaAvgVal > entityChangeStdDev) {
                        classes.push('increase-big');
                      } else if (deltaAvgVal < -1 * entityChangeStdDev) {
                        classes.push('decrease-big');
                      }
                    }

                    // Add field name class for field-specific styling
                    if (classes.length > 0) {
                      classes.push('revenueChange');
                    }

                    return classes.join(' ');
                  };

                  return (
                    <>
                      {metrics.map((metric) => {
                        // Special handling for Revenue Change
                        if (metric.label === 'Revenue Change') {
                          return (
                            <tr key="revenueChange" style={{ backgroundColor: '#f8f9fa', fontSize: '0.85em' }}>
                              <td></td>
                              <td style={{ paddingLeft: '30px', fontStyle: 'italic' }}>Revenue Change</td>
                              {/* Average column - show dash or hide based on showAverageColumn */}
                              {showAverageColumn && (
                                <td style={{ textAlign: 'right', backgroundColor: '#e9ecef', fontWeight: 'bold' }}>-</td>
                              )}
                              {dateColumns.map((date: string, idx: number) => {
                                const change = calculateRowRevenueChange(date, idx);
                                const highlightClasses = getEntityRevenueChangeHighlightingClasses(date, idx);
                                return (
                                  <td
                                    key={date}
                                    className={highlightClasses}
                                    style={{ textAlign: 'right' }}
                                    title={change !== null ? `Revenue Change: ${formatPercent(change)}` : 'No data'}
                                  >
                                    {change !== null ? formatPercent(change) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        }

                        const avgValue = metric.calculator();

                        // Calculate standard deviation and ranges for this metric
                        const today = new Date().toISOString().split('T')[0];
                        const mostRecentDate = dateColumns[0];
                        const shouldExcludeMostRecent = mostRecentDate === today;
                        const metricHistoricalValues: number[] = [];

                        dateColumns.forEach((date: string, index: number) => {
                          if (shouldExcludeMostRecent && index === 0) {
                            return;
                          }
                          const dayData = dataByDate.get(date);

                          // For calculated fields, we need to compute the value instead of looking for it in raw data
                          let val = dayData ? dayData[metric.field] : null;

                          // Special calculations for derived metrics (matching the cell value calculation below)
                          if (dayData) {
                            if (metric.field === 'apptapNetRevenue') {
                              const revenue = dayData.revenueApptap || 0;
                              const payout = dayData.revenuePublisher || 0;
                              const cost = dayData.reportedClickCost || 0;
                              val = revenue - payout - cost;
                            } else if (metric.field === 'publisherTrackedCreditRate') {
                              const tracked = dayData.clicksTracked || 0;
                              const received = dayData.clicksReceived || 0;
                              val = received !== 0 ? tracked / received : null;
                            } else if (metric.field === 'trackedCreditRate') {
                              const credited = dayData.conversionsCredited || 0;
                              const received = dayData.conversionsReceived || 0;
                              val = received !== 0 ? credited / received : null;
                            } else if (metric.field === 'averageCR') {
                              const conversions = Math.floor(dayData.conversionsReceived || 0);
                              const clicks = viewMode === 'demand'
                                ? Math.floor(dayData.clicksTracked || 0)
                                : Math.floor(dayData.clicksReceived || 0);
                              val = clicks !== 0 ? conversions / clicks : null;
                            } else if (metric.field === 'avgCPA') {
                              const revenue = Math.floor(dayData.revenueApptap || 0);
                              const conversions = Math.floor(dayData.conversionsReceived || 0);
                              val = conversions !== 0 ? revenue / conversions : null;
                            } else if (metric.field === 'eCPC') {
                              const revenue = Math.floor(dayData.revenueApptap || 0);
                              const clicks = viewMode === 'demand'
                                ? Math.floor(dayData.clicksTracked || 0)
                                : Math.floor(dayData.clicksReceived || 0);
                              val = clicks !== 0 ? revenue / clicks : null;
                            } else if (metric.field === 'wastedClickPcnt') {
                              const wasted = dayData.wastedClicks || 0;
                              const tracked = dayData.clicksTracked || 0;
                              val = tracked !== 0 ? wasted / tracked : null;
                            } else if (metric.field === 'convertingProductsPcnt') {
                              const working = dayData.workingDistinctProducts || 0;
                              const total = dayData.distinctProducts || 0;
                              val = total !== 0 ? working / total : null;
                            } else if (metric.field === 'convertingCampaignsPcnt') {
                              const working = dayData.workingDistinctCampaigns || 0;
                              const total = dayData.distinctCampaigns || 0;
                              val = total !== 0 ? working / total : null;
                            }
                          }

                          if (val !== null && val !== undefined && val !== '' && val !== 0) {
                            const num = typeof val === 'string' ? parseFloat(val) : val;
                            if (!isNaN(num) && num !== 0) {
                              metricHistoricalValues.push(num);
                            }
                          }
                        });

                        const metricStdDev = calculateStandardDeviation(metricHistoricalValues);
                        const metricDailyAvg = calculateMean(metricHistoricalValues);
                        const metricMin = metricHistoricalValues.length > 0 ? Math.min(...metricHistoricalValues) : 0;
                        const metricMax = metricHistoricalValues.length > 0 ? Math.max(...metricHistoricalValues) : 0;

                        // Create tooltip for average column
                        const metricExpectedMin = Math.max(metricDailyAvg - metricStdDev, 0);
                        const metricExpectedMax = metricDailyAvg + metricStdDev;
                        const metricTooltip = `Expected (stdev) Range:\n${metric.format(metricExpectedMin)} - ${metric.format(metricExpectedMax)}\nHistorical Range:\n${metric.format(metricMin)} - ${metric.format(metricMax)}\n${periodLabel} with data: ${metricHistoricalValues.length}`;

                        return (
                          <tr key={metric.field} style={{ backgroundColor: '#f8f9fa', fontSize: '0.85em' }}>
                            {/* Empty cell for expand button column */}
                            <td></td>

                            {/* Metric name cell (spans Traffic Source/Demand Source column) */}
                            <td style={{ paddingLeft: '30px', fontStyle: 'italic' }}>
                              {metric.label}
                            </td>

                            {/* 10d Average cell with tooltip - only show if there are multiple periods */}
                            {showAverageColumn && (
                              <td
                                style={{ textAlign: 'right', backgroundColor: '#e9ecef', fontWeight: 'bold' }}
                                title={metricTooltip}
                              >
                                {(avgValue !== null && avgValue !== 0) ? metric.format(avgValue) : '-'}
                              </td>
                            )}

                            {/* Date value cells - aligned with main table date columns */}
                            {dateColumns.map((date: string) => {
                              const dayData = dataByDate.get(date);
                              let value = dayData ? dayData[metric.field] : null;

                              // Special calculations to match Python dashboard (utils/__init__.py lines 49-54)
                              // These calculations use the div() function which returns null if division fails
                              if (dayData) {
                                // Approx Net Revenue: revenueApptap - revenuePublisher - reportedClickCost (line 61-62)
                                if (metric.field === 'apptapNetRevenue') {
                                  const revenue = dayData.revenueApptap || 0;
                                  const payout = dayData.revenuePublisher || 0;
                                  const cost = dayData.reportedClickCost || 0;
                                  value = revenue - payout - cost;
                                }
                                // Tracked %: clicksTracked / clicksReceived (line 69-70)
                                else if (metric.field === 'publisherTrackedCreditRate') {
                                  const tracked = dayData.clicksTracked || 0;
                                  const received = dayData.clicksReceived || 0;
                                  value = received !== 0 ? tracked / received : null;
                                }
                                // Credited %: conversionsCredited / conversionsReceived (line 71-72)
                                else if (metric.field === 'trackedCreditRate') {
                                  const credited = dayData.conversionsCredited || 0;
                                  const received = dayData.conversionsReceived || 0;
                                  value = received !== 0 ? credited / received : null;
                                }
                                // Average CR: conversions / clicks
                                // Demand: conversionsReceived / clicksTracked (line 43-44)
                                // Publisher: conversionsReceived / clicksReceived (line 49-50)
                                // NOTE: Python div() converts to int before dividing
                                else if (metric.field === 'averageCR') {
                                  const conversions = Math.floor(dayData.conversionsReceived || 0);
                                  const clicks = viewMode === 'demand'
                                    ? Math.floor(dayData.clicksTracked || 0)
                                    : Math.floor(dayData.clicksReceived || 0);
                                  value = clicks !== 0 ? conversions / clicks : null;
                                }
                                // Effective CPA: revenueApptap / conversionsReceived
                                // Both demand (line 45-46) and publisher (line 51-52) use conversionsReceived
                                // NOTE: Python div() converts to int before dividing (int(3.85) = 3)
                                else if (metric.field === 'avgCPA') {
                                  const revenue = Math.floor(dayData.revenueApptap || 0);
                                  const conversions = Math.floor(dayData.conversionsReceived || 0);
                                  value = conversions !== 0 ? revenue / conversions : null;
                                }
                                // Effective CPC: revenueApptap / clicks
                                // Demand: revenueApptap / clicksTracked (line 47-48)
                                // Publisher: revenueApptap / clicksReceived (line 53-54)
                                // NOTE: Python div() converts to int before dividing
                                else if (metric.field === 'eCPC') {
                                  const revenue = Math.floor(dayData.revenueApptap || 0);
                                  const clicks = viewMode === 'demand'
                                    ? Math.floor(dayData.clicksTracked || 0)
                                    : Math.floor(dayData.clicksReceived || 0);
                                  value = clicks !== 0 ? revenue / clicks : null;
                                }
                                // Wasted Clicks %: wastedClicks / clicksTracked (demand view only)
                                else if (metric.field === 'wastedClickPcnt') {
                                  const wasted = dayData.wastedClicks || 0;
                                  const tracked = dayData.clicksTracked || 0;
                                  value = tracked !== 0 ? wasted / tracked : null;
                                }
                                // Converting Product %: workingDistinctProducts / distinctProducts
                                else if (metric.field === 'convertingProductsPcnt') {
                                  const working = dayData.workingDistinctProducts || 0;
                                  const total = dayData.distinctProducts || 0;
                                  value = total !== 0 ? working / total : null;
                                }
                                // Converting Campaign %: workingDistinctCampaigns / distinctCampaigns
                                else if (metric.field === 'convertingCampaignsPcnt') {
                                  const working = dayData.workingDistinctCampaigns || 0;
                                  const total = dayData.distinctCampaigns || 0;
                                  value = total !== 0 ? working / total : null;
                                }
                              }

                              // Show '-' for null/undefined/empty string/zero (matching old dashboard behavior)
                              const displayValue = (value === null || value === undefined || value === '' || value === 0) ? null : value;
                              return (
                                <td
                                  key={date}
                                  style={{ textAlign: 'right' }}
                                  title={displayValue !== null ? `${metric.label}: ${metric.format(displayValue)}` : 'No data'}
                                >
                                  {displayValue !== null ? metric.format(displayValue) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
